import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../context/PermissionContext';

/**
 * Quick Actions bar — the most-used actions in one click.
 *
 * Progressive disclosure:
 *   - isNewUser (products < 3): shows only "core" actions + "More actions" link
 *   - established users: shows all actions
 *
 * Each action is gated by permission (hidden, not disabled, if not allowed).
 */

const actions = [
  {
    id: 'new-sale',
    label: 'New Sale',
    description: 'Record a transaction',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    ),
    permission: 'create_sale',
    path: '/sales',
    color: '#6C63FF',
    isPrimary: true,
    isCore: true,
    storeTypes: ['small_retail', 'supermarket', 'jewellery'],
  },
  {
    id: 'new-booking',
    label: 'New Booking',
    description: 'Create rental booking',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    permission: 'create_rental',
    path: '/bookings',
    color: '#8B5CF6',
    isPrimary: true,
    isCore: true,
    storeTypes: ['bridal_rental'],
  },
  {
    id: 'add-product',
    label: 'Add Product',
    description: 'Add to catalogue',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <line x1="12" y1="12" x2="12" y2="18" /><line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
    permission: 'create_product',
    path: '/products',
    color: '#0EA5E9',
    isCore: true,
  },
  // Advanced — hidden for new users
  {
    id: 'create-campaign',
    label: 'Campaign',
    description: 'Reach your customers',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 11l19-9-9 19-2-8-8-2z" />
      </svg>
    ),
    permission: 'view_customers',
    path: '/campaigns',
    color: '#F59E0B',
    isCore: false,
  },
  {
    id: 'add-expense',
    label: 'Expense',
    description: 'Record a cost',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z" />
        <line x1="8" y1="8" x2="16" y2="8" /><line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    ),
    permission: 'manage_expenses',
    path: '/expenses',
    color: '#EF4444',
    isCore: false,
  },
  {
    id: 'view-reports',
    label: 'Reports',
    description: 'View analytics',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    permission: 'view_reports',
    path: '/reports',
    color: '#22C55E',
    isCore: false,
  },
];

const DashboardQuickActions = ({ storeType, isNewUser = false }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [showAll, setShowAll] = useState(false);

  const visible = actions.filter(action => {
    if (action.permission && !hasPermission(action.permission)) return false;
    if (action.storeTypes && !action.storeTypes.includes(storeType)) return false;
    return true;
  });

  const coreActions     = visible.filter(a => a.isCore);
  const advancedActions = visible.filter(a => !a.isCore);

  // Which actions to actually render
  const toRender = (isNewUser && !showAll) ? coreActions : visible;

  if (toRender.length === 0 && advancedActions.length === 0) return null;

  return (
    <div className="dashboard-section dashboard-section--actions">
      <div className="quick-actions-grid">
        {toRender.map(action => (
          <button
            key={action.id}
            className={`quick-action-card${action.isPrimary ? ' quick-action-card--primary' : ''}`}
            onClick={() => navigate(action.path)}
            style={{ '--action-color': action.color }}
          >
            {action.isPrimary && (
              <span className="quick-action-card__badge">Primary</span>
            )}
            <span className="quick-action-card__icon">{action.icon}</span>
            <span className="quick-action-card__label">{action.label}</span>
            <span className="quick-action-card__desc">{action.description}</span>
          </button>
        ))}
      </div>

      {/* Progressive disclosure toggle — only for new users with hidden advanced actions */}
      {isNewUser && advancedActions.length > 0 && (
        <button
          className="quick-actions-more"
          onClick={() => setShowAll(s => !s)}
        >
          {showAll
            ? '▲ Hide extra actions'
            : `▾ More actions (${advancedActions.length})`
          }
        </button>
      )}
    </div>
  );
};

export default DashboardQuickActions;
